import { useMemo } from "react";
import { produce } from "immer";
import { InfoIcon } from "lucide-react";

import { getText } from "shared/lib";
import {
  TreePropertyType,
  type TreeDescriptor,
  type TrunkTypeTreePropertyDescriptor,
} from "./lib";

import { Button, Separator } from "shared/components";
import { FieldSet } from "./treeDialog/FieldSet";
import { DBHField } from "./treeDialog/DBHField";
import { CircumferenceField } from "./treeDialog/CircumferenceField";
import { HeightField } from "./treeDialog/HeightField";
import { DistanceField } from "./treeDialog/DistanceField";
import { ExcavationField } from "./treeDialog/ExcavationField";
import { LocationField } from "./treeDialog/LocationField";
import { TreeDialog } from "./treeDialog/TreeDialog";
import { TreeMeasurementsDialogContainer } from "./treeMeasurementsHelp";

export interface DiggingTreeDialogProps {
  onChangeTreeDescriptors?: (treeDescriptors: Array<TreeDescriptor>) => void;
  onClickDelete?: (treeDescriptor: TreeDescriptor) => void;
  onClickNext?: (treeDescriptor: TreeDescriptor) => void;
  onClickPrev?: (treeDescriptor: TreeDescriptor) => void;
  onClickSubmit?: (treeDescriptor: TreeDescriptor) => void;
  onOpenChange?: (open: boolean) => void;
  open?: boolean;
  treeDescriptor: TreeDescriptor | undefined | null;
  treeDescriptors: Array<TreeDescriptor>;
}

export function DiggingTreeDialog({
  onChangeTreeDescriptors,
  onClickDelete,
  onClickNext,
  onClickPrev,
  onClickSubmit,
  onOpenChange,
  open,
  treeDescriptor,
  treeDescriptors,
}: DiggingTreeDialogProps) {
  const dbhPropertyExists = treeDescriptor?.properties.has(
    TreePropertyType.dbh
  );

  const circumferencePropertyExists = treeDescriptor?.properties.has(
    TreePropertyType.circumference,
  );

  const heightPropertyExists = treeDescriptor?.properties.has(
    TreePropertyType.height,
  );

  const trunkType = treeDescriptor?.properties.get(
    TreePropertyType.trunk_type,
  );

  const handleChangeTreeDescriptor = (
    changedTreeDescriptor: TreeDescriptor,
  ) => {
    onChangeTreeDescriptors?.(
      produce(treeDescriptors, (draft) => {
        const index = draft.findIndex(
          (td) => td.treeId === changedTreeDescriptor?.treeId,
        );
        if (index > -1) {
          draft[index] = { ...changedTreeDescriptor };
        }
      }),
    );
  };

  const handleClickDelete = () => {
    if (treeDescriptor) {
      onClickDelete?.(treeDescriptor);
    }
    onOpenChange?.(false);
  };

  if (!treeDescriptor) return;

  return (
    <TreeDialog
      onChangeTreeDescriptor={handleChangeTreeDescriptor}
      onClickDelete={handleClickDelete}
      onClickNext={onClickNext}
      onClickPrev={onClickPrev}
      onClickSubmit={onClickSubmit}
      onOpenChange={onOpenChange}
      open={open}
      subTitle={getText(
        "You're not removing or pruning this tree, but your proposed works are close to it.",
      )}
      treeDescriptor={treeDescriptor}
      treeDescriptors={treeDescriptors}
    >
      {(dbhPropertyExists || circumferencePropertyExists) && (
        <>
          <FieldSet
            title={
              <span className="flex items-center gap-2">
                {getText("Measurements")}{" "}
                <TreeMeasurementsDialogContainer
                  treeDescriptor={treeDescriptor}
                >
                  {(setOpen) => (
                    <Button
                      className="text-bodysm gap-1"
                      onClick={() => setOpen(true)}
                      size="tight"
                      variant="link"
                    >
                      <InfoIcon />
                      {getText("More info")}
                    </Button>
                  )}
                </TreeMeasurementsDialogContainer>
              </span>
            }
            subTitle={getText(
              "Providing accurate measurements helps us better assess your permit requirements.",
            )}
          >
            {dbhPropertyExists && (
              <DBHField
                isMultiStem={trunkType?.value === "multi"}
                onChange={handleChangeTreeDescriptor}
                treeDescriptor={treeDescriptor}
              />
            )}

            {circumferencePropertyExists && (
              <CircumferenceField
                isMultiStem={trunkType?.value === "multi"}
                onChange={handleChangeTreeDescriptor}
                treeDescriptor={treeDescriptor}
              />
            )}

            {heightPropertyExists && (
              <HeightField
                onChange={handleChangeTreeDescriptor}
                treeDescriptor={treeDescriptor}
              />
            )}
          </FieldSet>

          <Separator orientation="horizontal" />
        </>
      )}

      <FieldSet title={getText("Location")}>
        <DistanceField
          onChange={handleChangeTreeDescriptor}
          treeDescriptor={treeDescriptor}
        />

        <LocationField
          onChange={handleChangeTreeDescriptor}
          treeDescriptor={treeDescriptor}
        />
      </FieldSet>

      <Separator orientation="horizontal" />

      <ExcavationField
        onChange={handleChangeTreeDescriptor}
        treeDescriptor={treeDescriptor}
      />
    </TreeDialog>
  );
}
